import {
    Card,
    CardContent,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Menu,
    Typography,
    TextField,
    Switch
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import EuroSymbolIcon from '@material-ui/icons/EuroSymbol';
import NumberFormat from 'react-number-format';
import { cancelBill, cancelPayment, getHistory, getInvoice, getPaymentMethods, update, exportPdf } from './api';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import banks from 'common/banks';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { MERCHANT_TYPE } from 'common/merchant';
import { toCent } from 'common/money';
import HistoryCard from './HistoryCard';
import StateCard from './StateCard';
import { BILL_STATE, EXTRA_OPTIONS } from 'common/billing';
import style from './style';
import { useParams, useSearchParams } from 'react-router-dom';
import { FormSkeleton, HistoryCardSkeleton, StateCardSkeleton } from './components';
import { empty } from '../../common/validators';

const useStyles = makeStyles(theme => style(theme));

const CustomTextField = props => {
    const { onChange, ...other } = props;

    return (
        <TextField
            {...other}
            size="small"
            onChange={onChange}
            variant="outlined"
            fullWidth
            required
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        <EuroSymbolIcon />
                    </InputAdornment>
                )
            }}
        />
    );
};

const filter = createFilterOptions();

/* ── Option tile with editable price (when checked) ───── */
const OptionTile = ({ option, checked, price, onToggle, onPriceChange, classes, disabled }) => {
    const { key, label, Icon } = option;
    return (
        <div className={`${classes.optionTile} ${checked ? classes.optionTileActive : ''}`}>
            <div
                className={classes.optionTileHeader}
                onClick={() => !disabled && onToggle(key, !checked)}
                role="button"
                tabIndex={0}>
                <div className={classes.optionTileLeft}>
                    <div
                        className={`${classes.optionTileIcon} ${
                            checked ? classes.optionTileIconActive : ''
                        }`}>
                        <Icon style={{ fontSize: 16 }} />
                    </div>
                    <Typography className={classes.optionTileLabel}>{label}</Typography>
                </div>
                <Switch
                    disabled={disabled}
                    className={classes.optionSwitch}
                    color="primary"
                    size="small"
                    checked={checked}
                    onClick={e => e.stopPropagation()}
                    onChange={e => !disabled && onToggle(key, e.target.checked)}
                    inputProps={{ 'aria-label': label }}
                />
            </div>

            {checked && (
                <div className={classes.optionPriceSlot}>
                    <Typography className={classes.optionPriceLabel}>Prix</Typography>
                    <NumberFormat
                        value={price ?? ''}
                        customInput={TextField}
                        size="small"
                        disabled={disabled}
                        fullWidth
                        variant="outlined"
                        className={classes.optionPriceInput}
                        onValueChange={v => onPriceChange(key, v.value)}
                        thousandsGroupStyle="fr"
                        thousandSeparator={' '}
                        decimalSeparator={','}
                        type="text"
                        allowedDecimalSeparators={[',', '.']}
                        allowNegative={false}
                        decimalScale={2}
                        isNumericString
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Typography variant="caption" color="textSecondary">
                                        €
                                    </Typography>
                                </InputAdornment>
                            )
                        }}
                        onClick={e => e.stopPropagation()}
                    />
                </div>
            )}
        </div>
    );
};

const BillLink = ({ number }) => {
    const [anchor, setAnchor] = useState(null);
    const { id } = useParams();

    return (
        <>
            <span
                style={{ cursor: 'pointer', color: '#3f51b5', textDecoration: 'underline' }}
                onClick={e => setAnchor(e.currentTarget)}>
                #{number}
            </span>
            <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
                <MenuItem
                    onClick={() => {
                        exportPdf(number);
                        setAnchor(null);
                    }}>
                    Voir le PDF
                </MenuItem>
                {id !== number ? (
                    <MenuItem
                        component={RouterLink}
                        to={`/bill/edit/${number}`}
                        onClick={() => setAnchor(null)}>
                        Modifier la facture
                    </MenuItem>
                ) : null}
            </Menu>
        </>
    );
};

const renderCommentText = text =>
    text.split(/(#[A-Z0-9]+)/g).map((part, i) =>
        /^#[A-Z0-9]+$/.test(part)
            ? <BillLink key={i} number={part.slice(1)} />
            : part
    );

const V2 = () => {
    const { id } = useParams();
    const classes = useStyles();
    const [loading, setLoading] = useState(true);
    const [amount, setAmount] = useState('');
    const [startAt, setStartAt] = useState(null);
    const [endAt, setEndAt] = useState(null);
    const [meters, setMeters] = useState(1);
    const [unitPrice, setUnitPrice] = useState(1);
    const [comment, setComment] = useState(null);
    const [internalComments, setInternalComments] = useState([]);
    const [paymentMethod, setPaymentMethod] = useState('');
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [withBank, setWithBank] = useState(false);
    const [bank, setBank] = useState(null);
    const [bankCheck, setBankCheck] = useState(null);
    const [bill, setBill] = useState({});
    const [history, setHistory] = useState([]);
    const [weeks, setWeeks] = useState(1);
    const [initialSnapshot, setInitialSnapshot] = useState(null);
    const [extras, setExtras] = useState({
        water: bill.has_water,
        electricity: bill.has_electricity,
        truck: bill.has_truck
    });
    const [extraPrices, setExtraPrices] = useState({
        water: bill.water_price ?? '',
        electricity: bill.electricity_price ?? '',
        truck: bill.truck_price ?? ''
    });

    const handleExtraToggle = (key, value) => {
        setExtras(prev => ({ ...prev, [key]: value }));
    };

    const handleExtraPriceChange = (key, value) => {
        setExtraPrices(prev => ({ ...prev, [key]: value }));
    };

    const handleFilterOptionAutocomplete = (options, params, key) => {
        const filtered = filter(options, params);

        if (params.inputValue !== '') {
            filtered.push({
                inputValue: params.inputValue,
                [key]: `Ajouter "${params.inputValue}"`
            });
        }

        return filtered;
    };

    const handleOptionLabelAutocomplement = (option, key) => {
        if (typeof option === 'string') {
            return option;
        }
        if (option.inputValue) {
            return option.inputValue;
        }

        return option[key];
    };

    useEffect(() => {
        setLoading(true);
    }, [id]);

    const applyInvoiceData = data => {
        const resolvedStartAt = data.type === MERCHANT_TYPE.subscriber ? data.start_at.date.split(' ')[0] : null;
        const resolvedEndAt = data.type === MERCHANT_TYPE.subscriber ? data.end_at.date.split(' ')[0] : null;
        setAmount(data.amount);
        setInternalComments(data.comment ?? []);
        setUnitPrice(data.unit_price);
        setPaymentMethod(data.payment_method);
        setWithBank(data.payment_method === 2);
        setBank(data.payment_method === 2 ? data.bank : null);
        setBankCheck(data.payment_method === 2 ? data.bank_check : null);
        setBill({ ...data });
        setStartAt(resolvedStartAt);
        setEndAt(resolvedEndAt);
        setMeters(data.meters);
        setWeeks(data.weeks);
        setExtras({ water: data.has_water, electricity: data.has_electricity, truck: false });
        setExtraPrices({
            water: data.water_price ?? '',
            electricity: data.electricity_price ?? '',
            truck: data.truck_price ?? ''
        });
        setInitialSnapshot({
            meters: data.meters,
            unitPrice: data.unit_price,
            paymentMethod: data.payment_method,
            bank: data.payment_method === 2 ? data.bank : null,
            bankCheck: data.payment_method === 2 ? data.bank_check : null,
            weeks: data.weeks,
            startAt: resolvedStartAt,
            endAt: resolvedEndAt,
            water: data.has_water,
            waterPrice: data.water_price ?? '',
            electricity: data.has_electricity,
            electricityPrice: data.electricity_price ?? '',
            truck: false,
            truckPrice: data.truck_price ?? ''
        });
    };

    useEffect(() => {
        getPaymentMethods()
            .then(data => setPaymentMethods(data))
            .catch(err => alert(err));

        getHistory(id)
            .then(data => setHistory(data))
            .catch(err => alert(err));

        getInvoice(id)
            .then(applyInvoiceData)
            .catch(err => alert(err))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        getInvoice(id)
            .then(applyInvoiceData)
            .catch(err => alert(err))
            .finally(() => setLoading(false));
    }, [loading]);

    useEffect(() => setWithBank(parseInt(paymentMethod) === 2), [paymentMethod]);

    useEffect(() => {
        const m = meters === '' ? bill.meters : meters;
        const p = unitPrice === '' ? bill.unit_price : unitPrice;
        const w = weeks === '' ? bill.weeks : weeks;
        const waterPrice = extras.water
            ? extraPrices.water === ''
                ? bill.water_price
                : parseFloat(extraPrices.water)
            : 0;

        const electricityPrice = extras.electricity
            ? extraPrices.electricity === ''
                ? bill.electricity_price
                : parseFloat(extraPrices.electricity)
            : 0;

        const truckPrice = extras.truck
            ? extraPrices.truck === ''
                ? bill.truck_price
                : parseFloat(extraPrices.truck)
            : 0;

        setAmount(
            (toCent(m * p * w) +
                toCent(waterPrice) +
                toCent(electricityPrice) +
                toCent(truckPrice)) /
                100
        );
    }, [
        meters,
        weeks,
        unitPrice,
        extras.electricity,
        extraPrices.electricity,
        extras.water,
        extraPrices.water,
        extras.truck,
        extraPrices.truck
    ]);

    const onCancelPayment = () => {
        cancelPayment(id)
            .catch(err => alert(err))
            .finally(() => setLoading(true));
    };

    const onCancelBill = () => {
        cancelBill(id)
            .catch(err => alert(err))
            .finally(() => setLoading(true));
    };

    const hasChanged = () => {
        if (!initialSnapshot) return false;
        return (
            meters !== initialSnapshot.meters ||
            unitPrice !== initialSnapshot.unitPrice ||
            paymentMethod !== initialSnapshot.paymentMethod ||
            bank !== initialSnapshot.bank ||
            bankCheck !== initialSnapshot.bankCheck ||
            weeks !== initialSnapshot.weeks ||
            startAt !== initialSnapshot.startAt ||
            endAt !== initialSnapshot.endAt ||
            extras.water !== initialSnapshot.water ||
            extraPrices.water !== initialSnapshot.waterPrice ||
            extras.electricity !== initialSnapshot.electricity ||
            extraPrices.electricity !== initialSnapshot.electricityPrice ||
            extras.truck !== initialSnapshot.truck ||
            extraPrices.truck !== initialSnapshot.truckPrice ||
            (!empty(comment) && Boolean(comment))
        );
    };

    const onConfirm = (withCopy = false) => {
        update(id, {
            meters,
            unitPrice,
            water: extras.water ? extraPrices.water : null,
            electricity: extras.electricity ? extraPrices.electricity : null,
            paymentMethod,
            bank: withBank ? bank : null,
            bankCheck: withBank ? bankCheck : null,
            comment: empty(comment) || !comment ? null : comment,
            weeks,
            startAt,
            endAt,
            withCopy,
        })
            .catch(err => alert(err))
            .finally(() => setLoading(true));
    };

    return (
        <div className={classes.pageWrapper}>
            <div className={classes.header}>
                <IconButton
                    className={classes.backBtn}
                    component={RouterLink}
                    to="/bills/non-subscribers/view"
                    size="small"
                    aria-label="Retour">
                    <ArrowBackIcon fontSize="small" />
                </IconButton>
                <div>
                    <Typography className={classes.headerTitle}>Modification de facture</Typography>
                    <Typography className={classes.headerSub}>
                        Modifier les informations modifiables et le règlement
                    </Typography>
                </div>
            </div>

            <Grid container spacing={2} className={classes.bodyGrid}>
                <Grid item xs={12} md={8} className={classes.rightStickyCol}>
                    <div className={classes.infoBanner}>
                        <div className={classes.infoBannerIcon}>
                            <InfoOutlinedIcon style={{ fontSize: 16 }} />
                        </div>
                        <div className={classes.infoBannerContent}>
                            <Typography className={classes.infoBannerTitle}>
                                À propos de la modification d'une facture
                            </Typography>
                            <Typography variant="caption">
                                Pour garantir la traçabilité comptable, toute modification crée une
                                nouvelle facture et annule simultanément la facture en cours.
                                L'ancienne facture reste consultable{' '}
                                <a href="/bills/canceled/view">dans l'historique à cette endroit</a>
                                .
                            </Typography>
                        </div>
                    </div>
                    {loading ? (
                        <FormSkeleton classes={classes} />
                    ) : (
                        <Card>
                            <CardContent>
                                <Typography className={classes.sectionLabel}>
                                    Détail de la facture
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <TextField
                                            disabled={bill.state === BILL_STATE.canceled}
                                            fullWidth
                                            label="Nombre de mètres"
                                            required
                                            name="meters"
                                            value={meters}
                                            variant="outlined"
                                            size="small"
                                            onChange={e => {
                                                const value = e.target.value;
                                                if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                                    setMeters(value);
                                                }
                                            }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <Typography
                                                            variant="body2"
                                                            color="textSecondary">
                                                            mètres
                                                        </Typography>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <NumberFormat
                                            disabled={bill.state === BILL_STATE.canceled}
                                            label="Prix unitaire"
                                            value={unitPrice}
                                            customInput={CustomTextField}
                                            allowedToChangePrice={true}
                                            onValueChange={v => setUnitPrice(v.value)}
                                            thousandsGroupStyle="fr"
                                            thousandSeparator={' '}
                                            decimalSeparator={','}
                                            type={'text'}
                                            allowedDecimalSeparators={[',', '.', '?', ';']}
                                            allowNegative={false}
                                            decimalScale={2}
                                            isNumericString
                                        />
                                    </Grid>
                                </Grid>
                                {bill.type === MERCHANT_TYPE.subscriber ? (
                                    <>
                                        <Typography
                                            className={classes.sectionLabel}
                                            style={{ marginTop: 20 }}>
                                            Période et nombre de semaine
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={4}>
                                                <TextField
                                                    disabled={bill.state === BILL_STATE.canceled}
                                                    fullWidth
                                                    label="Nombre de semaine"
                                                    required
                                                    name="weeks"
                                                    value={weeks}
                                                    variant="outlined"
                                                    size="small"
                                                    onChange={e => {
                                                        const value = e.target.value;
                                                        if (
                                                            value === '' ||
                                                            /^\d*\.?\d*$/.test(value)
                                                        ) {
                                                            setWeeks(value);
                                                        }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={4}>
                                                <TextField
                                                    disabled={bill.state === BILL_STATE.canceled}
                                                    fullWidth
                                                    label="Date début période"
                                                    required
                                                    type="date"
                                                    name="start_at"
                                                    value={startAt}
                                                    variant="outlined"
                                                    size="small"
                                                    onChange={e => setStartAt(e.target.value)}
                                                    InputLabelProps={{
                                                        shrink: true
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={4}>
                                                <TextField
                                                    disabled={bill.state === BILL_STATE.canceled}
                                                    fullWidth
                                                    label="Date fin période"
                                                    required
                                                    type="date"
                                                    name="end_at"
                                                    value={endAt}
                                                    variant="outlined"
                                                    size="small"
                                                    onChange={e => setEndAt(e.target.value)}
                                                    InputLabelProps={{
                                                        shrink: true
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </>
                                ) : null}

                                <Typography
                                    className={classes.sectionLabel}
                                    style={{ marginTop: 20 }}>
                                    Options supplémentaires
                                </Typography>
                                <div className={classes.optionsGrid}>
                                    {EXTRA_OPTIONS.map(option => (
                                        <OptionTile
                                            disabled={bill.state === BILL_STATE.canceled}
                                            key={option.key}
                                            option={option}
                                            checked={extras[option.key]}
                                            price={extraPrices[option.key]}
                                            onToggle={handleExtraToggle}
                                            onPriceChange={handleExtraPriceChange}
                                            classes={classes}
                                        />
                                    ))}
                                </div>

                                {bill.state !== BILL_STATE.unpaid ? (
                                    <>
                                        <Typography
                                            className={classes.sectionLabel}
                                            style={{ marginTop: 20 }}>
                                            Réglèment
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Mode de paiement"
                                                    required
                                                    disabled={bill.state === BILL_STATE.canceled}
                                                    name="paymentMethod"
                                                    value={paymentMethod}
                                                    variant="outlined"
                                                    size="small"
                                                    onChange={e => setPaymentMethod(e.target.value)}
                                                    select>
                                                    {paymentMethods.map(item => (
                                                        <MenuItem key={item.id} value={item.id}>
                                                            {item.label}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Autocomplete
                                                    disabled={!withBank}
                                                    required={withBank}
                                                    options={banks}
                                                    value={bank}
                                                    freeSolo
                                                    label="Banque"
                                                    size="small"
                                                    autoHighlight
                                                    selectOnFocus
                                                    clearOnBlur
                                                    handleHomeEndKeys
                                                    onChange={(_, newValue) => setBank(newValue)}
                                                    filterOptions={(options, params) =>
                                                        handleFilterOptionAutocomplete(
                                                            options,
                                                            params,
                                                            'title'
                                                        )
                                                    }
                                                    getOptionLabel={option =>
                                                        handleOptionLabelAutocomplement(
                                                            option,
                                                            'title'
                                                        )
                                                    }
                                                    renderOption={option => option.title}
                                                    renderInput={params => (
                                                        <TextField
                                                            fullWidth
                                                            {...params}
                                                            label="Banque"
                                                            required={withBank}
                                                            size="small"
                                                            disabled={!withBank}
                                                            variant="outlined"
                                                            name="banque"
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Numéro de chèque"
                                                    disabled={!withBank}
                                                    required={withBank}
                                                    name="bankCheck"
                                                    value={bankCheck}
                                                    variant="outlined"
                                                    size="small"
                                                    onChange={e => setBankCheck(e.target.value)}
                                                />
                                            </Grid>
                                        </Grid>
                                    </>
                                ) : null}

                                <Typography
                                    className={classes.sectionLabel}
                                    style={{ marginTop: 20 }}>
                                    Information complémentaire
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            disabled={bill.state === BILL_STATE.canceled}
                                            label="Commentaire"
                                            minRows={5}
                                            maxRows={10}
                                            helperText="Ajouter une note interne sur cette facture"
                                            name="comment"
                                            value={comment}
                                            variant="outlined"
                                            onChange={event => setComment(event.target.value)}
                                        />
                                    </Grid>
                                </Grid>
                                {internalComments.length > 0 ? (
                                    <div className={classes.notesList}>
                                        {internalComments
                                            .filter(text => typeof text === 'string')
                                            .map((text, i) => (
                                                <div key={i} className={classes.noteBody}>
                                                    <Typography className={classes.noteContent}>
                                                        {renderCommentText(text)}
                                                    </Typography>
                                                </div>
                                            ))}
                                    </div>
                                ) : null}
                            </CardContent>
                        </Card>
                    )}
                </Grid>
                <Grid item xs={12} md={4}>
                    {loading ? (
                        <>
                            <StateCardSkeleton classes={classes} />
                            <HistoryCardSkeleton classes={classes} />
                        </>
                    ) : (
                        <>
                            <StateCard
                                bill={bill}
                                meters={meters}
                                unitPrice={unitPrice}
                                paymentMethod={paymentMethod}
                                startAt={startAt}
                                endAt={endAt}
                                weeks={weeks}
                                extraPrices={extraPrices}
                                extras={extras}
                                amount={amount}
                                onCancelPayment={onCancelPayment}
                                onCancelBill={onCancelBill}
                                onConfirm={onConfirm}
                                confirmDisabled={!hasChanged()}
                            />

                            {/* ── Historique des modifications ─────────── */}
                            <HistoryCard
                                history={history}
                                billNumber={bill.number}
                                classes={classes}
                            />
                        </>
                    )}
                </Grid>
            </Grid>
        </div>
    );
};

export default V2;
